import { groupBy } from 'lodash';
import { useMemo } from 'react';

import {
  GroupSectionData,
  SectionRowContentComponent,
} from '@ui/ElementList/list.model';
import { GroupSectionsBuilder } from '@ui/ElementList/NestedElementList';

import { OneOfListElements } from '@shared/models/project.model';
import { filterListElementsContributingToVersionTotals } from '@shared/helpers/element-list-group-contributors.helpers';
import { sumConversionFactors } from '@shared/helpers/conversion-factors.helpers';
import { useProposalsResultRecords } from '@features/proposals/use-proposals.hooks';
import { useSelectedVersion } from '@client/store/ui';

import {
  ElementGroupingOption,
  getElementGroupingHelpers,
  GroupSectionMeta,
} from '@features/elements/element-grouping.model';
import {
  useFilterResultsBySelectedLifecycles,
  useVersionResultRecord,
} from '@features/results/use-results.hooks';
import {
  baselineResultsForGroupSum,
  proposalResultsForGroupSum,
} from '@features/proposals/ElementListRow/element-list-live-co2.helpers';
import { useLiveProductA1A3ByElementId } from '@features/proposals/ElementListRow/use-element-list-live-co2.hook';
import { getActiveRootElementIdsInProposal } from '@shared/helpers/proposal.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useOpenFocusGroup } from '@client/hooks/use-element-focus.hook';

export type GroupSectionProps = {
  RowContentComponent: SectionRowContentComponent<
    OneOfListElements,
    GroupSectionMeta
  >;
  groupSectionBuilder: GroupSectionsBuilder<GroupSectionMeta>;
};

export const useGroupSectionProps = (
  groupById: ElementGroupingOption,
): GroupSectionProps | undefined => {
  const resultsRecord = useVersionResultRecord(undefined, false);
  const proposalRecords = useProposalsResultRecords(false);
  const filterResults = useFilterResultsBySelectedLifecycles();
  const version = useSelectedVersion();
  const selectedProposalId = useSelectedProposalId();
  const openFocusGroup = useOpenFocusGroup();
  const liveProductA1A3 = useLiveProductA1A3ByElementId();

  return useMemo(() => {
    const groupHelpers = getElementGroupingHelpers(groupById);
    if (!groupHelpers) return undefined;
    const { getGroupKey, getGroupLabel, orderKeys } = groupHelpers.helpers;

    const activeIdsByProposal: Record<string, Set<string>> = {};
    if (version) {
      activeIdsByProposal[version.id] = new Set(
        filterListElementsContributingToVersionTotals(
          version.elements.filter(isElement),
          version,
        ).map((el) => el.id),
      );
      if (selectedProposalId) {
        activeIdsByProposal[selectedProposalId] = new Set(
          getActiveRootElementIdsInProposal(version, selectedProposalId, false),
        );
      }
    }

    const scenarioIdsForGroupSums = new Set<string>([version?.id ?? '']);
    if (selectedProposalId) scenarioIdsForGroupSums.add(selectedProposalId);

    const builder = (
      root: OneOfListElements[],
    ): GroupSectionData<OneOfListElements, GroupSectionMeta>[] => {
      const map = groupBy(root, (el) => getGroupKey(el));
      const orderedKeys = orderKeys(Object.keys(map));
      return orderedKeys.map((key) => {
        const items = map[key] ?? [];
        const itemsForVersionSum =
          filterListElementsContributingToVersionTotals(items, version);
        const resultsSum = sumConversionFactors(
          ...itemsForVersionSum.map((el) => {
            const stored = resultsRecord[el.id] ?? {};
            return baselineResultsForGroupSum(
              stored,
              liveProductA1A3.get(el.id),
              selectedProposalId,
              filterResults,
            );
          }),
        );
        const proposalResultsSum = Object.fromEntries(
          Object.entries(proposalRecords)
            .filter(([proposalId]) => scenarioIdsForGroupSums.has(proposalId))
            .map(([proposalId, record]) => {
              const activeSet = activeIdsByProposal[proposalId];
              const sum = sumConversionFactors(
                ...items
                  .filter((el) => activeSet?.has(el.id))
                  .map((el) => {
                    const stored = record?.[el.id] ?? {};
                    return proposalResultsForGroupSum(
                      stored,
                      liveProductA1A3.get(el.id),
                      { proposalId, selectedProposalId },
                      filterResults,
                    );
                  }),
              );
              return [proposalId, sum];
            }),
        );
        return {
          key,
          label: getGroupLabel(key),
          RowContentComponent: groupHelpers.RowContentComponent,
          items: items,
          meta: { resultsSum, proposalResultsSum },
        };
      });
    };
    return {
      RowContentComponent: groupHelpers.RowContentComponent,
      groupSectionBuilder: builder,
      onSectionDoubleClick: openFocusGroup,
    };
  }, [
    groupById,
    resultsRecord,
    proposalRecords,
    filterResults,
    version,
    selectedProposalId,
    liveProductA1A3,
    openFocusGroup,
  ]);
};
